import { isDefined } from "@clipboard-health/util-ts";
import { isBefore } from "date-fns";

import { DayOfWeek, DEFAULT_TIME_RANGE } from "./constants";

const MILLISECONDS_PER_MINUTE = 60 * 1000;
const MINUTES_PER_HOUR = 60;
const LAST_VALID_TIME_IN_MINUTES = 23 * MINUTES_PER_HOUR + 59;

interface Availability {
  dayOfWeek: DayOfWeek;
  start: number;
  end: number;
}

interface TimeSlot {
  dayOfWeek: DayOfWeek;
  isAvailable: boolean;
  slots: Array<{
    startTime: string;
    endTime: string;
  }>;
}

type TimeRange = TimeSlot["slots"][number];

function formatMinutesAsHHmm(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function formatTime(time: string): string {
  const date = new Date(`1970-01-01T${time}`);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function hasOverlappingTimeSlots(slots: Array<{ startTime: string; endTime: string }>) {
  for (let index = 0; index < slots.length; index++) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    for (let index_ = index + 1; index_ < slots.length; index_++) {
      const slot1 = slots[index];
      const slot2 = slots[index_];

      const start1 = new Date(`1970-01-01T${slot1.startTime}`);
      const end1 = new Date(`1970-01-01T${slot1.endTime}`);
      const start2 = new Date(`1970-01-01T${slot2.startTime}`);
      const end2 = new Date(`1970-01-01T${slot2.endTime}`);

      if (isBefore(start1, end2) && isBefore(start2, end1)) {
        return {
          hasOverlap: true,
          message: `Time interval ${formatTime(slot1.startTime)}-${formatTime(
            slot1.endTime
          )} should not overlap with ${formatTime(slot2.startTime)}-${formatTime(slot2.endTime)}`,
        };
      }
    }
  }

  return { hasOverlap: false, message: "" };
}

export function validateStartBeforeEnd(slot: { startTime: string; endTime: string }) {
  const start = new Date(`1970-01-01T${slot.startTime}`);
  const end = new Date(`1970-01-01T${slot.endTime}`);

  if (!isBefore(start, end)) {
    return {
      isValid: false,
      message: `Invalid time range: Start time ${formatTime(
        slot.startTime
      )} can't be later than end time ${formatTime(slot.endTime)}`,
    };
  }

  return { isValid: true, message: "" };
}

export function getNextInterviewTimeSlot(currentSlots: TimeRange[]): TimeRange | undefined {
  const lastSlot = currentSlots.reduce<TimeRange | undefined>((latestSlot, slot) => {
    const slotEndMs = getValidTimeSlotEndMs(slot);

    if (!isDefined(slotEndMs)) {
      return latestSlot;
    }

    if (!isDefined(latestSlot)) {
      return slot;
    }

    const latestSlotEndMs = getValidTimeSlotEndMs(latestSlot);
    return !isDefined(latestSlotEndMs) || slotEndMs > latestSlotEndMs ? slot : latestSlot;
  }, undefined);

  if (!isDefined(lastSlot)) {
    return { ...DEFAULT_TIME_RANGE };
  }

  const lastEndMinutes = parseHHmmToMs(lastSlot.endTime) / MILLISECONDS_PER_MINUTE;
  const startMinutes = lastEndMinutes + MINUTES_PER_HOUR;
  const endMinutes = startMinutes + MINUTES_PER_HOUR;

  if (endMinutes > LAST_VALID_TIME_IN_MINUTES) {
    return undefined;
  }

  return {
    startTime: formatMinutesAsHHmm(startMinutes),
    endTime: formatMinutesAsHHmm(endMinutes),
  };
}

function getValidTimeSlotEndMs(slot: TimeRange): number | undefined {
  const slotStartMs = parseHHmmToMs(slot.startTime);
  const slotEndMs = parseHHmmToMs(slot.endTime);

  if (!Number.isFinite(slotStartMs) || !Number.isFinite(slotEndMs) || slotEndMs <= slotStartMs) {
    return undefined;
  }

  return slotEndMs;
}

function formatToHHmm(milliseconds: number): string {
  const baseDate = new Date(0);
  baseDate.setUTCMilliseconds(milliseconds);

  const hours = baseDate.getUTCHours().toString().padStart(2, "0");
  const minutes = baseDate.getUTCMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
}

export function transformAvailabilityToTimeSlots(availability: Availability[]): TimeSlot[] {
  const daysInOrder: DayOfWeek[] = [
    DayOfWeek.SUNDAY,
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
    DayOfWeek.SATURDAY,
  ];

  const availabilityByDay = daysInOrder.reduce<Record<DayOfWeek, Availability[]>>(
    (accumulator, day) => {
      accumulator[day] = [];
      return accumulator;
    },

    {} as Record<DayOfWeek, Availability[]>
  );

  availability.forEach((item) => {
    availabilityByDay[item.dayOfWeek].push(item);
  });

  const timeSlots: TimeSlot[] = daysInOrder.map((day) => {
    const dayAvailability = availabilityByDay[day];

    if (dayAvailability.length === 0) {
      return {
        dayOfWeek: day,
        isAvailable: false,
        slots: [],
      };
    }

    return {
      dayOfWeek: day,
      isAvailable: true,
      slots: dayAvailability.map((slot) => ({
        startTime: formatToHHmm(slot.start),
        endTime: formatToHHmm(slot.end),
      })),
    };
  });

  return timeSlots;
}

export function parseHHmmToMs(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 * 60 * 1000 + minutes * 60 * 1000;
}

export function transformTimeSlotsToAvailability(timeSlots: TimeSlot[]): Availability[] {
  const availability: Availability[] = [];

  for (const daySlot of timeSlots) {
    if (daySlot.isAvailable) {
      for (const slot of daySlot.slots) {
        availability.push({
          dayOfWeek: daySlot.dayOfWeek,
          start: parseHHmmToMs(slot.startTime),
          end: parseHHmmToMs(slot.endTime),
        });
      }
    }
  }

  return availability;
}
